import React, { createContext, FC, useCallback, useContext, useEffect, useRef } from 'react';
import { MainSource } from '../../../constants/Source';
import { SystemFolderId } from '../../../constants/SystemFolderId';
import { MediaManagerSDKFile } from '../../media-manager-sdk';
import { getItemFromServerResponse } from '../../uploadingFile';
import { useSources } from '../useSources';
import { IRedirectAfterFirstFileUploadedContext, IUseRedirectAfterFirstFileUploaded } from './types';
import { useUploadingFiles } from './useUploadingFiles';

const RedirectAfterFirstFileUploadedContext = createContext<IRedirectAfterFirstFileUploadedContext>({
  getDidRedirectToPrivateMedia: () => false,
  setDidRedirectToPrivateMedia: () => {},
});

export const RedirectAfterFirstFileUploadedProvider: FC = ({ children }) => {
  const didRedirectToPrivateMediaRef = useRef(false);

  const setDidRedirectToPrivateMedia = useCallback(
    (didRedirectToPrivateMedia) => (didRedirectToPrivateMediaRef.current = didRedirectToPrivateMedia),
    [],
  );
  const getDidRedirectToPrivateMedia = useCallback(() => didRedirectToPrivateMediaRef.current, []);

  return (
    <RedirectAfterFirstFileUploadedContext.Provider
      value={{ setDidRedirectToPrivateMedia, getDidRedirectToPrivateMedia }}
    >
      {children}
    </RedirectAfterFirstFileUploadedContext.Provider>
  );
};

export const useRedirectAfterFirstFileUploaded = ({
  openFolderById,
  source,
}: IUseRedirectAfterFirstFileUploaded): void => {
  const { getDidRedirectToPrivateMedia, setDidRedirectToPrivateMedia } = useContext(
    RedirectAfterFirstFileUploadedContext,
  );

  const didRedirectToPrivateMedia = getDidRedirectToPrivateMedia();

  const { isSiteFiles } = useSources({ source });
  const { uploadedFiles } = useUploadingFiles();

  const [firstUploadedFile] = uploadedFiles;

  useEffect(() => {
    if (!firstUploadedFile) {
      setDidRedirectToPrivateMedia(false);
      return;
    }

    if (firstUploadedFile && isSiteFiles) {
      setDidRedirectToPrivateMedia(true);
      return;
    }

    const canRedirect = !didRedirectToPrivateMedia;

    if (!isSiteFiles && canRedirect) {
      const uploadedFileData = getItemFromServerResponse<MediaManagerSDKFile>(firstUploadedFile);

      openFolderById(
        uploadedFileData.folderId || SystemFolderId.SiteFiles,
        MainSource.SiteFiles,
        '',
        firstUploadedFile.serverResponse.data.id,
      );

      setDidRedirectToPrivateMedia(true);
    }
  }, [uploadedFiles.length, isSiteFiles]);
};
